import { lazy } from "react";
import { SettingsIcon, StarIcon } from "@signet/ui";
import { defineWebModule, lazyPage, type PortalFrameProps } from "@signet/web-kit";
import { str, windowSearch } from "./api.ts";
import { PingsNowCard } from "./now-cards.tsx";
import { OnboardingTrackerPanel } from "./onboarding-tracker.tsx";
import "./onboarding-tracker.css";
import "./hub.css";
import "./creators.css";

// Loaded when first opened (web-kit lazy.ts).
const CreatorPage = lazyPage(() => import("./creator.tsx"), "CreatorPage");
const NotesPage = lazyPage(() => import("./notes-page.tsx"), "NotesPage");
const CreatorsSettingsPage = lazyPage(() => import("./settings-page.tsx"), "CreatorsSettingsPage");
// A creator's portal, and People's Creators tab. own-page.tsx (the earnings
// page creators had) is not routed for now: it returns behind View stats.
const PortalFrame = lazy(() => import("./portal/frame.tsx").then((m) => ({ default: m.PortalFrame as React.ComponentType<PortalFrameProps> })));
const PortalSettings = lazyPage(() => import("./portal/settings.tsx"), "PortalSettings");
// The welcome tour's pictures, loaded with the rest of the portal's kit, never in the staff app.
const WelcomeArt = lazy(() => import("@signet/web-kit/portal").then((m) => ({ default: m.WelcomeArt })));
const FinishArt = lazy(() => import("@signet/web-kit/portal").then((m) => ({ default: m.FinishArt })));
const CreatorsPeopleTab = lazy(() => import("./portal/people-tab.tsx").then((m) => ({ default: m.CreatorsPeopleTab })));

/* ──────────────────────────────────────────────────────────────────────────
   The web half of Creators: the creators hub, lifted, looking exactly as it
   did (docs/DECISIONS.md, "Creators looks exactly like the hub").

   The hub had two pages, the dashboard and settings, and so does this:
   /creators is the dashboard opened on the first creator, as the hub's front
   page was, /creators/<name> the same page on that creator, and
   /creators/settings the hub's settings tabs. The addresses earlier versions
   of Signet gave to what are now tabs (links, onboarding, sign-ins) open
   those tabs, so links to them still land. /creators/notes has no page on
   the hub and is built from its pieces.

   The static pages are registered before the creator page, and TanStack
   ranks a static segment above a param anyway, so /creators/notes is the
   notes page and never a creator called NOTES.
   ────────────────────────────────────────────────────────────────────────── */

const creatorSearch = (raw: Record<string, unknown>) => ({ ...windowSearch(raw), overview: raw.overview === "open" ? "open" : undefined, tab: str(raw.tab) });
const settingsSearch = (raw: Record<string, unknown>) => ({ tab: str(raw.tab) });

export default defineWebModule({
  id: "creators",
  nav: { label: "Creators", icon: StarIcon, to: "/creators", needs: "creators.roster:view" },
  routes: [
    { path: "/creators", component: CreatorPage, needs: "creators.roster:view", validateSearch: creatorSearch },
    // Pings only need the roster, and a note that names you should be readable
    // from the Now card however narrow your grant. The notes feed itself asks
    // for creators.note:view inside the page.
    { path: "/creators/notes", component: NotesPage, needs: "creators.roster:view", validateSearch: (raw) => ({ q: str(raw.q) }) },
    // Settings shows each tab to whoever may open it, as the hub did, so the
    // page itself only needs the module; every tab's data is guarded on the
    // server by its own resource.
    { path: "/creators/settings", component: CreatorsSettingsPage, needs: "creators.roster:view", validateSearch: settingsSearch },
    { path: "/creators/onboarding", component: CreatorsSettingsPage, needs: "creators.onboarding:view" },
    { path: "/creators/links", component: CreatorsSettingsPage, needs: "creators.link:view" },
    // Which accounts are a creator's own login. A People decision, so it asks
    // for People rather than for anything in this module.
    { path: "/creators/sign-ins", component: CreatorsSettingsPage, needs: "signet.people:view" },
    { path: "/creators/$creator", component: CreatorPage, needs: "creators.roster:view", validateSearch: creatorSearch },
  ],
  commands: [
    { id: "creators.roster", label: "Creators", to: "/creators", needs: "creators.roster:view", hint: "The roster", keywords: "hub dashboard earnings talent models" },
    { id: "creators.notes", label: "Creator notes", to: "/creators/notes", needs: "creators.roster:view", hint: "Creators", keywords: "pings mentions waiting on me" },
    { id: "creators.onboarding", label: "Onboarding template", to: "/creators/onboarding", needs: "creators.onboarding:view", hint: "Creators settings", keywords: "checklist template new creator" },
    { id: "creators.links", label: "Essential links", to: "/creators/links", needs: "creators.link:view", hint: "Creators settings", keywords: "sheets folders drive registry #" },
    { id: "creators.signins", label: "Creator sign-ins", to: "/creators/sign-ins", needs: "signet.people:view", hint: "Creators settings", keywords: "client login account bind own page" },
    { id: "creators.settings", label: "Creators settings", to: "/creators/settings", needs: "creators.roster:view", hint: "Colour, roster and credentials", keywords: "reporting api tracker google token instagram accent add creator archive" },
  ],
  // The quick bar's "<name>" and "<name> profile".
  creatorPages: [
    { id: "creators.page", label: "Profile", needs: "creators.roster:view", order: 0, keywords: "creator page hub dashboard overview notes", to: (name) => `/creators/${encodeURIComponent(name)}` },
  ],
  // What a creator's own sign-in sees: their portal, in its own frame, with
  // pages from every module that serves these accounts (Ideas, and Settings here).
  // Staff open the same pages as any creator from People (/preview/creators/<NAME>).
  bound: { path: "/creator", frame: PortalFrame, preview: true },
  portal: {
    for: "creators",
    pages: [{ path: "settings", label: "Settings", icon: SettingsIcon, order: 90, component: PortalSettings }],
    // The welcome tour's start and end, and making the dashboard theirs first (Jay and the boss, 2026-10-29).
    tour: [
      {
        id: "creators.welcome",
        order: 0,
        page: "",
        art: WelcomeArt,
        title: (first) => (first ? `Hi ${first}, welcome to your dashboard` : "Welcome to your dashboard"),
        body: "This is where you'll find viral video ideas picked for you. Here's a quick look around. It takes about a minute.",
      },
      { id: "creators.photo", order: 10, page: "settings", target: "photo", title: "Start with your photo", body: <>Tap <strong>Change photo</strong> to put your face on your dashboard. It shows in the menu and on Home.</> },
      {
        id: "creators.colours",
        order: 20,
        page: "settings",
        target: "colours",
        tryIt: ".cr-portal-swatch",
        tap: '[data-tour="swatch-try"]',
        title: "Pick your colours",
        body: "Tap any colour and the whole dashboard changes to match. Go on, try one. You can change it whenever you like.",
      },
      { id: "creators.finish", order: 1000, art: FinishArt, title: "You're all set", body: <>That's everything. You can take this tour again any time from <strong>Take the tour</strong> in the menu.</> },
    ],
  },
  peopleTabs: [{ id: "creators", label: "Creators", needs: "signet.people:view", order: 10, component: CreatorsPeopleTab }],
  nowCards: {
    "creators.pings": PingsNowCard,
  },
  // Ben's onboarding tracker, first on Now, in place of the stalled onboarding card (Ben, 2026-11-02 and 2026-11-04).
  nowPanels: [{ id: "creators.onboarding-tracker", needs: "creators.onboarding:edit", order: 10, wide: true, component: OnboardingTrackerPanel }],
});
